import React, { useState, useEffect } from 'react';
import { Button, Dropdown, Form } from 'react-bootstrap';
import { TfiAnnouncement } from 'react-icons/tfi';
import { TbEyeStar } from 'react-icons/tb';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import './anouncement.css';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A'; // Fallback for missing dates

  const date = new Date(dateString);
  if (isNaN(date)) return 'Invalid Date'; // Handle invalid date formats

  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

// Styled Dialog component
const CustomDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90%',
    margin: '10px',
    zIndex: theme.zIndex.drawer + 1,
  },
}));

// Styles for Dialog components
const dialogTitleStyle = {
  fontSize: '1.5rem',
  position: 'relative',
  paddingRight: '40px',
};

const dialogContentStyle = {
  fontSize: '1rem',
  lineHeight: '1.5',
  padding: '1.5rem',
};

const closeIconStyle = {
  position: 'absolute',
  right: 0,
  top: 0,
  margin: '10px',
  color: '#fff',
  fontSize: '1.5rem',
};

// ResponsiveDialog component for displaying announcements
function ResponsiveDialog({ open, onClose, announcement }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <CustomDialog
      fullScreen={fullScreen}
      open={open}
      onClose={onClose}
      aria-labelledby="responsive-dialog-title"
    >
      <DialogTitle id="responsive-dialog-title" style={dialogTitleStyle} className='header-announcement'>
        {announcement?.title || 'Announcement'}
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          style={closeIconStyle}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent style={dialogContentStyle}>
        <DialogContentText>
          <strong>Title:</strong> {announcement?.title} <br />
          <strong>Message:</strong> {announcement?.content} <br />
          <strong>For:</strong> {announcement?.category} <br />
          <strong>Admin</strong><br />
          <strong>Date:</strong> {formatDateTime(announcement?.createdAt)}
          <hr />
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </CustomDialog>
  );
}

// Main Announcement component
export default function Announcement() {
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [data, setData] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [searchQuery, setSearchQuery] = useState(''); // State for search query
  const [filteredData, setFilteredData] = useState([]);  // State to hold filtered data after search

  const userRole = localStorage.getItem('role') || 'All'; // Default to "All" if role not set
  const category = 'All'; // Fetch "All" categories
  // Fetch and filter announcements when search, role, category, or pagination changes
  useEffect(() => {
    const filterAnnouncements = async () => {
      try {
        const { data } = await axios.get('https://backend.ems.hiradentallab.com/api/announcements/filter/search', {
          params: {
            role: userRole,
            category: category,
            search: searchQuery,  // Pass the search query to backend
          },
        });

        setData(data);  // Set fetched data to the main data state
        setTotalPages(Math.ceil(data.length / rowsPerPage));  // Update total pages for pagination

        // Sort the fetched data so that search results appear at the top
        const sortedData = data.sort((a, b) => {
          const aSearchMatch = a.title.toLowerCase().includes(searchQuery.toLowerCase()) || a.content.toLowerCase().includes(searchQuery.toLowerCase());
          const bSearchMatch = b.title.toLowerCase().includes(searchQuery.toLowerCase()) || b.content.toLowerCase().includes(searchQuery.toLowerCase());

          if (aSearchMatch && !bSearchMatch) return -1;  // a has match, place it at the top
          if (!aSearchMatch && bSearchMatch) return 1;   // b has match, place it at the top
          return 0;  // If both or neither match, leave their positions unchanged
        });

        setFilteredData(sortedData);  // Set the sorted data into filteredData state
      } catch (error) {
        console.error('Error fetching announcements:', error);
      }
    };

    filterAnnouncements();
  }, [currentPage, userRole, category, searchQuery, rowsPerPage]);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const { data } = await axios.get(
          'https://backend.ems.hiradentallab.com/api/announcements/announcements/filter',
          {
            params: {
              role: userRole,
              category: category,
              search: searchQuery, // Pass search query to API
            },
          }
        );

        setData(data);
        setTotalPages(Math.ceil(data.length / rowsPerPage));
      } catch (error) {
        console.error('Error fetching announcements:', error);
      }
    };

    fetchAnnouncements();
  }, [currentPage, userRole, category, searchQuery]); // Add searchQuery to dependencies

  const handleClickOpen = (announcement) => {
    setSelectedAnnouncement(announcement);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const pageRange = () => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  };

  // Search change handler

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);  // Update search query state
  };
  
  const handleSearchSubmit = (e) => {
    e.preventDefault();  // Prevent page reload
    // Any additional functionality for submitting can go here
  };
  
  return (
    <>
      <div>
        <h1 className="announcement-headerrr">
          <TfiAnnouncement style={{ marginRight: '2px' }} />
          Announcement
        </h1>
        <hr />
        <div className="announcement-table-container">
  <div className="filter-container d-flex justify-content-between align-items-center">
    {/* Left Dropdown */}
    <div className="filter-left">
      <Dropdown className="d-inline mx-0" autoClose="inside">
        <Dropdown.Toggle id="dropdown-autoclose-inside">
          Show Entries
        </Dropdown.Toggle>
        <Dropdown.Menu className="custom-dropdown-menu">
          <Dropdown.Item href="#" onClick={() => rowsPerPage(15)}>15</Dropdown.Item>
          <Dropdown.Item href="#" onClick={() => rowsPerPage(25)}>25</Dropdown.Item>
          <Dropdown.Item href="#" onClick={() => rowsPerPage(50)}>50</Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>

    {/* Right Search Bar */}
    <div className="filter-right ms-auto">
      <Form className="d-flex" onSubmit={handleSearchSubmit}>
        <Form.Control
          type="search"
          placeholder="Search announcements..."
          aria-label="Search"
          className="search-bar"
          value={searchQuery}
          onChange={handleSearchChange}
          style={{ width: '250px', marginRight: '10px' }} // Adjusted width
        />
      </Form>
    </div>
  </div>
      <div className="table-responsive">
        <table className="announcement-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Message</th>
              <th>For</th>
              <th>Author</th>
              <th>Date</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
          {filteredData.length > 0 ? (
                  filteredData
                    .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                    .map((item) => (
                      <tr key={item._id}>
                        <td>{item.title}</td>
                        <td>{item.content}</td>
                        <td>{item.category}</td>
                        <td>{item.author || 'Admin'}</td>
                        <td>{formatDateTime(item.createdAt)}</td>
                        <td>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleClickOpen(item)}
                          >
                            <TbEyeStar />
                          </Button>
                        </td>
                      </tr>
                    ))
            ) : (
              <tr>
                <td colSpan="6">No announcements found.</td>
              </tr>
            )}
          </tbody>
          <tfoot className="table-footer">
            <tr className="announcement-table-footer">
              <td>Total</td>
              <td>{data.length}</td>
              <td>-</td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="pagination-container">
              <div>
                Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
                {Math.min(currentPage * rowsPerPage, data.length)} of{" "}
                {data.length} entries
              </div>
              <nav>
                <ul className="pagination">
                  <li
                    className={`page-item ${
                      currentPage === 1 ? "disabled" : ""
                    }`}
                  >
                    <a
                      href="/#"
                      className="page-link"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(currentPage - 1);
                      }}
                    >
                      Previous
                    </a>
                  </li>
                  {pageRange().map((pageNumber) => (
                    <li
                      key={pageNumber}
                      className={`page-item ${
                        currentPage === pageNumber ? "active" : ""
                      }`}
                    >
                      <a
                        href="/#"
                        className="page-link"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(pageNumber);
                        }}
                      >
                        {pageNumber}
                      </a>
                    </li>
                  ))}
                  <li
                    className={`page-item ${
                      currentPage === Math.ceil(data.length / rowsPerPage)
                        ? "disabled"
                        : ""
                    }`}
                  >
                    <a
                      href="/#"
                      className="page-link"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(currentPage + 1);
                      }}
                    >
                      Next
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      
      {selectedAnnouncement && (
        <ResponsiveDialog
          open={dialogOpen}
          onClose={handleClose}
          announcement={selectedAnnouncement}
        />
      )}
    </>
  );
}
